import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { logger } from './logger';
import type { ApiError } from './axios';
import { isTransientError } from './transient';
import { showReconnecting, hideReconnecting } from './connection';

/**
 * Type guard to check if error is a normalized ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'isNetworkError' in error;
}

/**
 * Check if error is a global error (401/429) that's already handled by axios interceptors
 */
export const isGlobalError = (error: unknown): boolean => {
  if (isApiError(error)) {
    return error.status === 401 || error.status === 429;
  }
  return false;
};

/**
 * Handle API errors with proper severity levels
 * - Network errors (backend down) → Sentry (alerts) + Logger
 * - 5xx: Server errors → Sentry (alerts) + Logger (dashboards)
 * - 4xx: Client errors → Logger only (informational)
 * - 401/429: Skipped (handled by axios interceptor)
 */
function handleApiError(error: unknown) {
  // Skip 401/429 - already handled by axios interceptor
  if (isGlobalError(error)) return;

  // Use normalized ApiError properties if available
  const apiError = isApiError(error) ? error : null;
  const status = apiError?.status;
  const url = apiError?.url;
  const method = apiError?.method;
  const code = apiError?.code;
  const isNetworkError = apiError?.isNetworkError ?? false;

  if (isTransientError(error)) {
    // The API is briefly unreachable - almost always a deploy swapping the
    // container. Not an incident: this used to fire one Sentry alert per failed
    // query per open tab on every single deploy, at sampleRate 1.0.
    logger.warn('Backend temporarily unreachable', { url, method, code, status });
  } else if (isNetworkError) {
    // Network error - backend is down, CORS issue, or connection refused
    Sentry.captureException(error, {
      extra: { url, method, code },
      tags: { errorType: 'network_error' },
    });
    logger.error('Network error - backend unreachable', error as Error, {
      url,
      method,
      code,
    });
  } else if (status && status >= 500) {
    // Server errors - send to BOTH Sentry (alerts) and Logger (dashboards)
    Sentry.captureException(error, {
      extra: { status, url, method },
      tags: { errorType: 'api_5xx' },
    });
    logger.error('API server error', error as Error, { status, url, method });
  } else if (status && status >= 400) {
    // Client errors (4xx) - just log as warning
    logger.warn('API client error', { status, url, method });
  }
}

/**
 * QueryClient with global error handling
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry on auth or rate limit errors
        if (isGlobalError(error)) {
          return false;
        }
        // failureCount is pre-increment, so this is 6 attempts. With the delay
        // below that spans ~11.5s of cover -- comfortably longer than the API
        // container swap. The previous default (4 attempts, ~7s) was actually
        // SHORTER than the outage it needed to survive.
        if (isTransientError(error)) {
          // This predicate runs on EVERY failed attempt, which is the only hook
          // that fires while retries are still in progress. QueryCache.onError
          // is not: it fires once, after the last attempt has given up.
          showReconnecting();
          return failureCount < 5;
        }
        return failureCount < 3;
      },
      retryDelay: (failureCount) => Math.min(500 * 2 ** failureCount, 4000),
    },
    mutations: {
      // Deliberately not retried. A POST whose response was lost may well have
      // been delivered, and replaying something like /counters/:id/increment
      // would apply it twice.
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      // Retries are exhausted by the time this runs, so the notice is no longer
      // true for THIS query. Clear it unless something else is still retrying.
      settleReconnecting();
      handleApiError(error);
    },
    onSuccess: () => settleReconnecting(),
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      settleReconnecting();
      handleApiError(error);
    },
    onSuccess: () => settleReconnecting(),
  }),
});

/**
 * Hide the "Reconnecting…" notice only once nothing is still retrying.
 *
 * One query succeeding does not mean the backend is back for everything: a
 * dismissal keyed purely on success would clear the notice while another query
 * was mid-backoff, making it flicker in and out.
 */
function settleReconnecting(): void {
  const stillRetrying = queryClient
    .getQueryCache()
    .getAll()
    .some((q) => q.state.fetchStatus === 'fetching' && q.state.fetchFailureCount > 0);

  if (!stillRetrying) hideReconnecting();
}
